import React from 'react';
import { Box, Grid, Card, CardContent, Typography, Button } from '@mui/material';
import { 
  SupervisorAccount, 
  Assignment, 
  TrendingUp, 
  Settings,
  People,
  AccountBalance,
  LocationOn
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const StatCard = ({ title, value, icon, color, onClick }) => (
  <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="overline">
            {title}
          </Typography>
          <Typography variant="h4" component="div" color={color}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ color: color, fontSize: 40 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { canPerformAction } = useAuth();

  const stats = {
    totalUsers: 156,
    totalApplications: 2450,
    activeWorkflows: 12,
    auditLogs: 5420,
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Admin Dashboard
      </Typography>
      
      <Typography variant="body1" color="text.secondary" mb={3}>
        System overview and administrative controls
      </Typography>

      {/* Admin Statistics */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Users"
            value="124"
            icon={<People />}
            color="primary.main"
            onClick={() => navigate('/admin/users')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active Applications"
            value="89"
            icon={<Assignment />}
            color="success.main"
            onClick={() => navigate('/applications')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Disbursed"
            value="₹2.5Cr"
            icon={<AccountBalance />}
            color="warning.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="System Health"
            value="98%"
            icon={<TrendingUp />}
            color="success.main"
          />
        </Grid>
      </Grid>

      {/* Admin Quick Actions */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Management
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Manage system users, roles, and permissions
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<SupervisorAccount />}
                onClick={() => navigate('/admin/users')}
              >
                Manage Users
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Workflow Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Configure application workflows and processes
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<Settings />}
                onClick={() => navigate('/admin/workflow')}
              >
                Configure Workflows
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                District Management
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Manage administrative hierarchy and geographical boundaries
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<LocationOn />}
                onClick={() => navigate('/admin/districts')}
              >
                Manage Districts
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Monitoring */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent System Activity
          </Typography>
          <Box>
            <Typography variant="body2" color="text.secondary">
              • User 'john_staff' processed 15 applications today
              <br />
              • New user 'inspector_abc' registered
              <br />
              • Workflow 'ex_gratia_v1.0' updated
              <br />
              • System backup completed successfully
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminDashboard;
